import React, { useCallback, useMemo, useState, PropsWithChildren } from 'react';
import { Breakpoint } from 'types/utils';
import { jc, jcm } from 'utils';
import { Styles } from './tiles-line-gallery.styles';

export type TilesLineGalleryProps = {
  tilesContent?: React.ReactElement[];
  breakpoint: Breakpoint;
} & React.HTMLAttributes<HTMLElement>;

const contentSizes = new Map<Breakpoint, number>([
  ['large', 9],
  ['medium', 7],
]);

export const TilesLineGallery: React.FC<TilesLineGalleryProps> = ({
  breakpoint,
  tilesContent = [],
  className = '',
  ...htmlProps
}) => {
  const [asMultiline, setAsMultiline] = useState<boolean>(false);

  const { showMoreBtnIsShowing, photosTailLength, maxContentSize } = useMemo(() => {
    const contentSize = contentSizes.get(breakpoint) ?? tilesContent.length;
    const tailLength = tilesContent.length - contentSize;

    return {
      showMoreBtnIsShowing: tailLength > 0 && !asMultiline,
      photosTailLength: tailLength,
      maxContentSize: contentSize,
    };
  }, [breakpoint, asMultiline, tilesContent.length]);

  const shMoreBtnClick = useCallback(() => setAsMultiline(true), []);

  return (
    <div className={jc(className, Styles.componentContainer)} {...htmlProps}>
      <div
        className={jcm({
          [Styles.contentContainer]: true,
          [Styles.contentContainerMLine]: asMultiline,
          [Styles.contentContainerLarge]: breakpoint === 'large',
          [Styles.contentContainerMedium]: breakpoint === 'medium',
          [Styles.contentContainerTablet]: breakpoint === 'tablet',
          [Styles.contentContainerMobile]: breakpoint === 'mobile',
        })}
      >
        {tilesContent.slice(0, maxContentSize).map((tileContent, idx) => (
          <GalleryTile key={idx} asMultiline={asMultiline}>
            {tileContent}
          </GalleryTile>
        ))}

        {asMultiline &&
          tilesContent.slice(maxContentSize).map((tileContent, id) => (
            <GalleryTile key={id} asMultiline={asMultiline}>
              {tileContent}
            </GalleryTile>
          ))}

        {showMoreBtnIsShowing && (
          <ShowMoreButton onClick={shMoreBtnClick}>{`+${photosTailLength}`}</ShowMoreButton>
        )}
      </div>
    </div>
  );
};

export const GalleryTile: React.FC<{ asMultiline: boolean } & PropsWithChildren> = ({
  asMultiline,
  children,
}) => {
  return (
    <div
      className={jcm({
        [Styles.tileContainer]: true,
        [Styles.tileContainerMLine]: asMultiline,
      })}
    >
      {children}
    </div>
  );
};

export const ShowMoreButton: React.FC<React.HTMLAttributes<HTMLDivElement>> = ({
  className = '',
  children,
  ...htmlProps
}) => {
  return (
    <div className={jc(className, Styles.showMoreButton)} {...htmlProps}>
      {children}
    </div>
  );
};
